import { isKnownAccount } from './chartOfAccounts';
import type { InvoiceLine, InvoiceLineKind } from './types';

/**
 * THE tax table. One entry decides, for a given kind of turnover, ALL of:
 *   - the rate that may be charged,
 *   - the revenue/expense account and the USt/VSt account the Fibu books it to,
 *   - the EN 16931 tax category the e-invoice (XRechnung/ZUGFeRD) must carry.
 *
 * This exists because a free percentage field cannot be made watertight by careful typing: an
 * invoice with 19% and 7% lines used to be exported as ONE row with a derived "15%" rate, and a
 * 7% sale was booked to the 19% revenue account. Both are fabricated data reaching the tax
 * advisor. With a code instead of a number, those states cannot be represented at all.
 */
export type TaxCode =
  | 'S19' // Inland, Regelsatz
  | 'S7' // Inland, ermäßigt
  | 'Z0' // Inland, steuerfrei
  | 'IG' // steuerfreie innergemeinschaftliche Lieferung (§ 4 Nr. 1b UStG)
  | 'EX' // steuerfreie Ausfuhrlieferung, Drittland
  | 'RC13B' // Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG)
  | 'D25A'; // Differenzbesteuerung für Gebrauchtgegenstände (§ 25a UStG)

/**
 * EN 16931 / UNTDID 5305 Steuerkategorien (BT-151 in der Zeile, BT-118 in der Aufteilung).
 *
 * BEFUND (09/2026, gefunden mit dem KoSIT-Pruefer): Hier stand zusaetzlich 'AA' ("ermaessigter
 * Satz") und der 7-%-Schluessel benutzte es. Die EN 16931 laesst aber nur die Teilmenge
 * S/E/K/G/AE/L/M/O/Z zu -- der Pruefer wies jede Rechnung mit einer 7-%-Zeile ab
 * (BR-CL-17/BR-CL-18, "tax categories MUST be coded using UNCL5305 code list").
 * Der ermaessigte Satz ist dort NICHT eine eigene Kategorie, sondern ebenfalls 'S' mit 7,00 %.
 */
export type XRechnungTaxCategory = 'S' | 'E' | 'K' | 'G' | 'AE';

export interface TaxCodeDefinition {
  readonly code: TaxCode;
  readonly label: string;
  readonly rate: number;
  /** Umsatzsteuer account for outgoing turnover ('' when no VAT arises). */
  readonly outputVatAccount: string;
  /** Vorsteuer account for incoming invoices ('' when no input tax arises). */
  readonly inputVatAccount: string;
  readonly xrechnungCategory: XRechnungTaxCategory;
  /** BT-120 — required by EN 16931 for every category other than S/AA. */
  readonly xrechnungExemptionReason: string;
  /**
   * DATEV Buchungsschlüssel. Empty means "the account carries the tax" (Automatikkonto).
   * NOT guessed: the chart is customised, so whether a given account is an Automatikkonto is a
   * question for the tax advisor. `exportValidation` surfaces the ones still to be confirmed.
   */
  readonly datevBuKey: string;
  /**
   * Margenbesteuerung: Bemessungsgrundlage ist NICHT der Rechnungsbetrag, sondern die Differenz
   * aus Verkaufs- und Einkaufspreis -- und die Steuer ist darin ENTHALTEN, wird also
   * herausgerechnet statt aufgeschlagen. Die Rechnung weist keine Steuer aus.
   */
  readonly marginScheme: boolean;
  /**
   * Der Steuersatz, mit dem die Marge herausgerechnet wird. Nur bei `marginScheme` belegt --
   * `rate` ist dort 0, weil nach aussen nichts ausgewiesen wird.
   */
  readonly marginRate: number;
}

export const taxCodeDefinitions: Readonly<Record<TaxCode, TaxCodeDefinition>> = {
  S19: {
    code: 'S19', label: 'Inland 19 %', rate: 19,
    outputVatAccount: '1776', inputVatAccount: '1576',
    xrechnungCategory: 'S', xrechnungExemptionReason: '', datevBuKey: '', marginScheme: false, marginRate: 0,
  },
  S7: {
    code: 'S7', label: 'Inland 7 %', rate: 7,
    outputVatAccount: '1771', inputVatAccount: '1571',
    xrechnungCategory: 'S', xrechnungExemptionReason: '', datevBuKey: '', marginScheme: false, marginRate: 0,
  },
  Z0: {
    code: 'Z0', label: 'Steuerfrei', rate: 0,
    outputVatAccount: '', inputVatAccount: '',
    xrechnungCategory: 'E', xrechnungExemptionReason: 'Steuerfreier Umsatz', datevBuKey: '', marginScheme: false, marginRate: 0,
  },
  IG: {
    code: 'IG', label: 'Innergem. Lieferung', rate: 0,
    outputVatAccount: '', inputVatAccount: '1573',
    xrechnungCategory: 'K',
    xrechnungExemptionReason: 'Steuerfreie innergemeinschaftliche Lieferung (§ 4 Nr. 1b UStG)',
    datevBuKey: '', marginScheme: false, marginRate: 0,
  },
  EX: {
    code: 'EX', label: 'Ausfuhr Drittland', rate: 0,
    outputVatAccount: '', inputVatAccount: '',
    xrechnungCategory: 'G',
    xrechnungExemptionReason: 'Steuerfreie Ausfuhrlieferung (§ 4 Nr. 1a UStG)',
    datevBuKey: '', marginScheme: false, marginRate: 0,
  },
  /**
   * Differenzbesteuerung (§ 25a UStG) -- der Kern des Gebrauchtmaschinengeschäfts.
   *
   * Wird ein Stapler von privat (oder von einem Kleinunternehmer) angekauft, gibt es keine
   * Vorsteuer. Beim Weiterverkauf wird deshalb nicht der ganze Preis besteuert, sondern nur die
   * MARGE -- und die Steuer ist darin enthalten (Marge x 19/119), nicht aufgeschlagen.
   *
   * Die Rechnung darf KEINE Umsatzsteuer gesondert ausweisen (§ 14a Abs. 6 UStG); ein Ausweis
   * würde nach § 14c geschuldet. Stattdessen ist der Hinweis „Gebrauchtgegenstände/Sonderregelung"
   * Pflicht. Der Käufer hat daraus keinen Vorsteuerabzug.
   *
   * `rate` ist bewusst 0 -- das ist der Satz, der nach aussen ausgewiesen wird. Der Satz, mit dem
   * die Marge herausgerechnet wird, steht in `marginRate`.
   *
   * Erlös- und Umsatzsteuerkonto bleiben LEER: welches SKR-03-Konto die Margensteuer trägt, ist
   * eine Frage an den Steuerberater -- der Kontenrahmen ist angepasst. Genau wie bei den
   * BU-Schlüsseln gilt: ein falsches Konto ist schlimmer als keines.
   */
  D25A: {
    code: 'D25A', label: 'Differenzbesteuerung § 25a', rate: 0,
    outputVatAccount: '', inputVatAccount: '',
    xrechnungCategory: 'E',
    xrechnungExemptionReason: 'Gebrauchtgegenstände/Sonderregelung',
    datevBuKey: '', marginScheme: true, marginRate: 19,
  },
  RC13B: {
    code: 'RC13B', label: 'Reverse Charge § 13b', rate: 0,
    outputVatAccount: '', inputVatAccount: '1577',
    xrechnungCategory: 'AE',
    xrechnungExemptionReason: 'Steuerschuldnerschaft des Leistungsempfängers (§ 13b UStG)',
    datevBuKey: '', marginScheme: false, marginRate: 0,
  },
};

export const allTaxCodes: readonly TaxCode[] = Object.keys(taxCodeDefinitions) as TaxCode[];

export function taxCodeDefinition(code: TaxCode): TaxCodeDefinition {
  return taxCodeDefinitions[code];
}

export function isTaxCode(value: string): value is TaxCode {
  return value in taxCodeDefinitions;
}

/**
 * Maps a bare percentage to a code — for data captured BEFORE tax codes existed. Returns null for
 * anything that is not a German rate, so the export can REFUSE it instead of inventing a
 * treatment. Note 0 % is ambiguous (steuerfrei? innergemeinschaftlich? § 13b?) and therefore only
 * ever resolves to the plain domestic Z0; the real distinction has to come from the document.
 */
export function taxCodeForRate(rate: number): TaxCode | null {
  if (rate === 19) return 'S19';
  if (rate === 7) return 'S7';
  if (rate === 0) return 'Z0';
  return null;
}

/**
 * Welcher Steuerschlüssel für diese Zeile gilt: der ausdrücklich gesetzte, sonst der aus dem Satz
 * abgeleitete. EINE Stelle, damit Beleg, E-Rechnung und Buchungsstapel nicht auseinanderlaufen.
 */
export function effectiveTaxCode(line: Pick<InvoiceLine, 'taxCode' | 'vatRate'>): TaxCode | null {
  return line.taxCode ?? taxCodeForRate(line.vatRate);
}

/**
 * Bemessungsgrundlage der Differenzbesteuerung: Verkaufspreis minus Einkaufspreis (§ 25a Abs. 3).
 * Verkauft man mit Verlust, ist die Marge NULL -- eine negative Marge mindert die Steuer nicht.
 */
export function marginBase(line: Pick<InvoiceLine, 'netAmount' | 'marginPurchasePrice'>): number {
  const einkauf = Math.max(0, Number(line.marginPurchasePrice) || 0);
  return Math.max(0, Math.round((line.netAmount - einkauf) * 100) / 100);
}

/**
 * Die in der Marge ENTHALTENE Umsatzsteuer -- herausgerechnet, nicht aufgeschlagen:
 * bei 19 % also Marge x 19/119. Der Kunde sieht sie nie, geschuldet wird sie trotzdem.
 */
export function marginVat(
  line: Pick<InvoiceLine, 'netAmount' | 'marginPurchasePrice'>,
  marginRate: number,
): number {
  const satz = Number(marginRate) || 0;
  if (satz <= 0) return 0;
  return Math.round(marginBase(line) * (satz / (100 + satz)) * 100) / 100;
}

/**
 * Which revenue account a line hits. Rules are ordered — the first match wins, so a specific
 * line kind beats the wildcard. Configurable ON PURPOSE: the chart currently has no separate
 * accounts for Miete/Verkauf/Service (only Spedition/Spezialtransporte are split out), and
 * inventing account numbers is exactly the mistake that must not happen. When the tax advisor
 * adds e.g. a Mieterlöskonto, it is entered here — no code change anywhere else.
 */
export interface RevenueAccountRule {
  readonly lineKind: InvoiceLineKind | '*';
  readonly taxCode: TaxCode;
  readonly account: string;
  readonly note: string;
}

export const revenueAccountRules: readonly RevenueAccountRule[] = [
  // Fracht/Transport hat im Kontenplan eigene Erlöskonten.
  { lineKind: 'freight', taxCode: 'S19', account: '8409', note: 'Speditionserlöse 19 % MWSt.' },
  { lineKind: 'freight', taxCode: 'S7', account: '8002', note: 'Umsatzerlöse 7% Spezialtransporte' },
  { lineKind: 'freight', taxCode: 'IG', account: '8411', note: 'Speditionserlöse EU 0% MWST.' },
  // Alles Übrige.
  { lineKind: '*', taxCode: 'S19', account: '8400', note: 'Erlöse (vom Kunden bestätigt: 19 % Inland)' },
  { lineKind: '*', taxCode: 'S7', account: '8001', note: 'Umsatzerlöse 7%' },
  { lineKind: '*', taxCode: 'Z0', account: '8110', note: 'sonst steuerfreie Erlöse' },
  { lineKind: '*', taxCode: 'IG', account: '8125', note: 'Steuerfreie innerg Lieferungen' },
  { lineKind: '*', taxCode: 'EX', account: '8100', note: 'Erlöse Drittland' },
  { lineKind: '*', taxCode: 'RC13B', account: '8337', note: 'Erlöse aus Leistungen § 13b' },
];

/** The revenue account for a line, or null when the combination has no account configured. */
export function revenueAccountFor(lineKind: InvoiceLineKind, code: TaxCode): string | null {
  const rule = revenueAccountRules.find((r) => (r.lineKind === lineKind || r.lineKind === '*') && r.taxCode === code);
  return rule?.account ?? null;
}

/**
 * Every account this module points at must exist in the chart — asserted by the unit tests, so a
 * typo here fails the build rather than reaching the Fibu.
 *
 * Deliberately NOT modelled yet: a separate Erlösschmälerungs-Konto (8700/8724/8725) and the
 * Forderungs-Sammelkonten (1410/1411/1412). A Gutschrift here always reverses a whole invoice, so
 * it belongs on the original revenue account in Haben — 8700 is for partial price reductions,
 * which FORQ cannot produce today. The Sammelkonto is assigned in the debtor's DATEV master data,
 * not per booking. Both would be speculative config, and a wrong account is worse than none.
 */
export function referencedAccounts(): readonly string[] {
  const accounts = new Set<string>();
  for (const definition of Object.values(taxCodeDefinitions)) {
    if (definition.outputVatAccount) accounts.add(definition.outputVatAccount);
    if (definition.inputVatAccount) accounts.add(definition.inputVatAccount);
  }
  for (const rule of revenueAccountRules) accounts.add(rule.account);
  return [...accounts].filter((account) => !isKnownAccount(account));
}
